import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import TitleHeader from '../../atoms/TitleHeader/TitleHeader'
import Constants from '../../../app/utils/constants'
import bgImage from '../../../assets/images/industrypage/expertiesbg.svg'

const cardData = Constants.fintechExpertiseCards

const headingGradientStyle = {
  background: 'linear-gradient(92.21deg, #EA892A 0%, #E35384 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}

const ExpertiseSection = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [cardOffset, setCardOffset] = useState(80)

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % cardData.length)
    }, 4200)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const setOffsetByViewport = () => {
      const width = window.innerWidth
      if (width >= 1536) {
        setCardOffset(300)
      } else if (width >= 1280) {
        setCardOffset(240)
      } else if (width >= 1024) {
        setCardOffset(180)
      } else if (width >= 768) {
        setCardOffset(120)
      } else {
        setCardOffset(80)
      }
    }

    setOffsetByViewport()
    window.addEventListener('resize', setOffsetByViewport)
    return () => window.removeEventListener('resize', setOffsetByViewport)
  }, [])

  return (
    <section className="relative mb-20 h-[250px] w-full overflow-visible text-white md:mb-24 md:h-[300px] lg:mb-28 lg:h-[370px] xl:mb-32 xl:h-[400px] 2xl:mb-36 2xl:h-[500px]">
      {/* Animated SVG background layer */}
      <div className="pointer-events-none absolute inset-0 z-0 select-none">
      <img
  src={bgImage}
  alt=""
  aria-hidden="true"
  className="h-full w-full scale-100 object-cover object-center md:scale-100 md:object-contain"
/>
      </div>

      {/* Content */}
      <div className="relative z-10 flex h-200px] flex-col items-center justify-start md:h-[230px] lg:h-[80px] xl:h-[120px] 2xl:h-[200px]">

        {/* Header */}
        <TitleHeader
          badgeText="EXPERTISE"
          titleLines={['Compliance Expertise']}
          wrapperClassName="flex flex-col items-center text-center mt-[5%] xl:mt-[3%] 2xl:mt-[3%]"
          titleClassName="text-3xl font-rajdhani font-semibold text-center md:text-4xl lg:text-5xl min-[1440px]:text-7xl"
          badgeClassName="px-10 py-3 text-2xl md:px-12 md:py-4 md:text-2xl min-[1440px]:px-14 min-[1440px]:text-[2rem]"
        />

        {/* Cards anchored to bottom */}
        <div 
          className="expertise-container absolute bottom-0 left-1/2 z-20 flex h-[300px] w-full -translate-x-1/2 translate-y-1/2 items-end justify-center md:h-[230px] lg:h-[700px]"
          style={{
            perspective: '1500px',
            transformStyle: 'preserve-3d',
          }}
        >
          {cardData.map((card, index) => {
            const positionIndex = (index - activeIndex + cardData.length) % cardData.length

            const isCenter = positionIndex === 0
            const isRight = positionIndex === 1
            const isLeft = positionIndex === cardData.length - 1
            const isHidden = !isCenter && !isRight && !isLeft

            const hiddenDirection = positionIndex < cardData.length / 2 ? 1 : -1
            const targetX = isCenter
              ? 0
              : isRight
                ? cardOffset
                : isLeft
                  ? -cardOffset
                  : hiddenDirection * (cardOffset * 1.7)

            return (
              <motion.div
                key={card.title}
                initial={false}
                animate={{
                  x: targetX,
                  y: isCenter ? 0 : 30,
                  scale: isCenter ? 1 : 0.9,
                  rotateY: 0,
                  z: isCenter ? 0 : -180,
                  opacity: isCenter ? 1 : isHidden ? 0 : 1,
                }}
                transition={{
                  x: { type: 'spring', stiffness: 72, damping: 30, mass: 1.15 },
                  y: { type: 'spring', stiffness: 72, damping: 30, mass: 1.15 },
                  scale: { type: 'spring', stiffness: 68, damping: 30, mass: 1.2 },
                  z: { type: 'spring', stiffness: 72, damping: 30, mass: 1.15 },
                  opacity: { duration: 0.65, ease: 'easeInOut' },
                }}
                style={{
                  zIndex: isCenter ? 30 : isLeft ? 20 : isRight ? 10 : 0,
                  position: 'absolute',
                }}
                className="w-[220px] md:w-[420px] lg:w-[520px] xl:w-[620px] 2xl:w-[720px] will-change-transform"
              >
                  <div className="rounded-[26px] overflow-hidden border border-white/10 bg-[#130C2F] shadow-[0_30px_100px_rgba(0,0,0,0.7)]">
                    {/* Top Border */}
                    <motion.div
                      animate={{
                        backgroundColor: isCenter ? '#745EF0' : '#EA892A',
                        height: '10px'
                      }}
                      className="w-full"
                      style={{
                        background: !isCenter ? 'linear-gradient(92.21deg, #EA892A 0%, #E35384 100%)' : undefined
                      }}
                    />

                    <div className="p-4 md:p-6 text-center h-[120px] md:h-[150px] lg:h-[180px] xl:h-[200px] 2xl:h-[250px] justify-center items-center flex flex-col">
                      <h3
                        className="font-rajdhani font-bold text-xl md:text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl"
                        style={headingGradientStyle}
                      >
                        {card.title}
                      </h3>

                      <p className="mt-2 xl:w-[85%] 2xl:w-[85%] font-gilroy-medium text-sm md:text-sm lg:text-lg xl:text-xl 2xl:text-2xl text-gray-400">
                        {card.description}
                      </p>
                    </div>
                  </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default ExpertiseSection
